import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { adminAPI, authAPI } from '../services/api';
import {
  TrendingUp,
  DollarSign,
  MousePointerClick,
  ShoppingCart,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Tag,
  Store
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days

  useEffect(() => {
    // Check if user is admin
    if (!authAPI.isAdmin()) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [dateRange, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Calculate date range
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      
      // Fetch analytics and stats
      const [analyticsData, statsData] = await Promise.all([
        adminAPI.getAnalytics(startDate, endDate),
        adminAPI.getStatsSummary()
      ]);
      
      setAnalytics(analyticsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className='mb-8 flex items-center justify-between'>
          <div>
            <h1 className='text-4xl font-bold text-gray-900 mb-2'>
              Analytics Dashboard
            </h1>
            <p className='text-gray-600'>
              Track dine indtægter og performance metrics
            </p>
          </div>
          <div className='flex gap-3'>
            <Button 
              variant="outline"
              onClick={() => navigate('/admin/discount-codes')}
              data-testid="manage-codes-btn"
            >
              <Tag className="w-4 h-4 mr-2" />
              Rabatkoder
            </Button>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className='mb-6 flex gap-2'>
          {['7', '30', '90', '365'].map((days) => (
            <button
              key={days}
              onClick={() => setDateRange(days)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                dateRange === days
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {days === '365' ? '1 år' : `${days} dage`}
            </button>
          ))}
        </div>

        {/* Summary Stats */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <Card className='border-none shadow-lg'>
            <CardContent className='p-6'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-gray-600 text-sm'>Total Klik</span>
                <MousePointerClick className='w-5 h-5 text-blue-600' />
              </div>
              <p className='text-3xl font-bold text-gray-900'>
                {analytics?.total_clicks?.toLocaleString() || 0}
              </p>
              <p className='text-sm text-gray-500 mt-1'>
                {stats?.total_clicks?.toLocaleString() || 0} lifetime
              </p>
            </CardContent>
          </Card>

          <Card className='border-none shadow-lg'>
            <CardContent className='p-6'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-gray-600 text-sm'>Conversions</span>
                <ShoppingCart className='w-5 h-5 text-green-600' />
              </div>
              <p className='text-3xl font-bold text-gray-900'>
                {analytics?.total_conversions || 0}
              </p>
              <p className='text-sm text-gray-500 mt-1'>
                {analytics?.conversion_rate || 0}% conversion rate
              </p>
            </CardContent>
          </Card>

          <Card className='border-none shadow-lg'>
            <CardContent className='p-6'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-gray-600 text-sm'>Pending Kommission</span>
                <Clock className='w-5 h-5 text-orange-600' />
              </div>
              <p className='text-3xl font-bold text-gray-900'>
                {analytics?.pending_commission?.toLocaleString() || 0} kr
              </p>
              <Badge variant='outline' className='mt-2'>
                Afventer godkendelse
              </Badge>
            </CardContent>
          </Card>

          <Card className='border-none shadow-lg'>
            <CardContent className='p-6'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-gray-600 text-sm'>Godkendt Kommission</span>
                <CheckCircle className='w-5 h-5 text-green-600' />
              </div>
              <p className='text-3xl font-bold text-gray-900'>
                {analytics?.approved_commission?.toLocaleString() || 0} kr
              </p>
              <p className='text-sm text-green-600 mt-1'>
                ✓ Klar til udbetaling
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Total Commission Card */}
        <Card className='border-none shadow-xl mb-8 bg-gradient-to-br from-blue-600 to-teal-500 text-white'>
          <CardContent className='p-8'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-blue-100 mb-2'>Total Kommission (Periode)</p>
                <p className='text-5xl font-bold'>
                  {analytics?.total_commission?.toLocaleString() || 0} kr
                </p>
                <p className='text-blue-100 mt-2'>
                  Lifetime: {stats?.total_commission?.toLocaleString() || 0} kr
                </p>
              </div>
              <DollarSign className='w-20 h-20 text-white opacity-20' />
            </div>
          </CardContent>
        </Card>

        {/* Top Shops */}
        <Card className='border-none shadow-lg mb-8'>
          <CardHeader>
            <CardTitle>Top Performende Butikker</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.top_shops && analytics.top_shops.length > 0 ? (
              <div className='space-y-4'>
                {analytics.top_shops.map((shop, index) => (
                  <div
                    key={shop.shop_id}
                    className='flex items-center justify-between p-4 bg-gray-50 rounded-lg'
                  >
                    <div className='flex items-center space-x-4'>
                      <span className='text-2xl font-bold text-gray-400'>
                        #{index + 1}
                      </span>
                      {shop.logo_url && (
                        <img
                          src={shop.logo_url}
                          alt={shop.name}
                          className='w-12 h-12 rounded-lg object-cover'
                        />
                      )}
                      <div>
                        <p className='font-semibold text-gray-900'>{shop.name}</p>
                        <p className='text-sm text-gray-500'>
                          {shop.clicks} klik
                        </p>
                      </div>
                    </div>
                    <TrendingUp className='w-5 h-5 text-green-600' />
                  </div>
                ))}
              </div>
            ) : (
              <p className='text-gray-500 text-center py-8'>
                Ingen data endnu. Start med at generere klik!
              </p>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className='border-none shadow-lg bg-blue-50'>
          <CardContent className='p-6'>
            <h3 className='font-semibold text-gray-900 mb-2'>
              📊 Sådan fungerer kommissions tracking
            </h3>
            <ul className='space-y-2 text-sm text-gray-700'>
              <li>• <strong>Klik</strong> - Hver gang en bruger klikker på en affiliate link</li>
              <li>• <strong>Conversions</strong> - Når en bruger foretager et køb (sporet af affiliate netværk)</li>
              <li>• <strong>Pending</strong> - Kommission der afventer godkendelse (typisk 30-90 dage)</li>
              <li>• <strong>Godkendt</strong> - Kommission der er blevet godkendt og kan udbetales</li>
            </ul>
            <div className='mt-4 p-4 bg-white rounded-lg border border-blue-200'>
              <p className='text-sm text-gray-600'>
                <strong>Note:</strong> For at få reelle conversions skal du tilføje dine affiliate API nøgler 
                i <code className='bg-gray-100 px-2 py-1 rounded'>/app/backend/affiliate_service.py</code>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
